<script setup>
import { Head, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import ManagementHeader from "../../../Components/Management/ManagementHeader.vue";
import Scheduler from "../../../Components/Management/Scheduler.vue";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";
import { Inertia } from "@inertiajs/inertia";

const props = defineProps([
    'providers',
    'selectedProvider',
    'filters',
    'flash'
]);

const route = window.route;
const toast = useToast();
const form = useForm({
    provider_id: props.selectedProvider?.uuid,
    working_hours: props.selectedProvider?.working_hours || [],
});

const selectedProviderUuid = ref(props.filters?.provider);

watch(() => props.filters?.provider, (newVal) => {
    if (newVal !== selectedProviderUuid.value) {
        selectedProviderUuid.value = newVal;
    }
});

const handleProviderChange = (e) => {
    selectedProviderUuid.value = e.value;
    Inertia.get(route('management.providers.scheduler'), { provider: e.value }, {
        preserveState: false,
        preserveScroll: true,
        only: ['selectedProvider', 'filters']
    });
};

watch(() => props.selectedProvider?.uuid, (newId) => {
    if (newId && newId !== form.provider_id) {
        form.provider_id = newId;
        form.working_hours = props.selectedProvider?.working_hours
            ? JSON.parse(JSON.stringify(props.selectedProvider.working_hours))
            : [];
    }
});

const submit = () => {
    if (!props.selectedProvider) return;

    // Client-side validation
    form.clearErrors();
    let hasClientErrors = false;
    
    form.working_hours.forEach((h, idx) => {
        if (h.is_workday) {
            if (!h.start_time) {
                form.setError(`working_hours.${idx}.start_time`, 'Beginn ist erforderlich');
                hasClientErrors = true;
            }
            if (!h.end_time) {
                form.setError(`working_hours.${idx}.end_time`, 'Ende ist erforderlich');
                hasClientErrors = true;
            }
            if (h.start_time && h.end_time && h.start_time >= h.end_time) {
                form.setError(`working_hours.${idx}.end_time`, 'Ende muss nach Beginn liegen');
                hasClientErrors = true;
            }

            if (h.customer_start_time || h.customer_end_time) {
                if (!h.customer_start_time) {
                    form.setError(`working_hours.${idx}.customer_start_time`, 'Kunden-Beginn ist erforderlich');
                    hasClientErrors = true;
                }
                if (!h.customer_end_time) {
                    form.setError(`working_hours.${idx}.customer_end_time`, 'Kunden-Ende ist erforderlich');
                    hasClientErrors = true;
                }
                if (h.customer_start_time && h.customer_end_time && h.customer_start_time >= h.customer_end_time) {
                    form.setError(`working_hours.${idx}.customer_end_time`, 'Kunden-Ende muss nach Kunden-Beginn liegen');
                    hasClientErrors = true;
                }
                if (h.customer_start_time && h.start_time && h.customer_start_time < h.start_time) {
                    form.setError(`working_hours.${idx}.customer_start_time`, 'Kunden-Beginn darf nicht vor Mitarbeiter-Beginn liegen');
                    hasClientErrors = true;
                }
                if (h.customer_end_time && h.end_time && h.customer_end_time > h.end_time) {
                    form.setError(`working_hours.${idx}.customer_end_time`, 'Kunden-Ende darf nicht nach Mitarbeiter-Ende liegen');
                    hasClientErrors = true;
                }
            }
        }
    });

    if (hasClientErrors) {
        toast.add({
            severity: 'error',
            summary: 'Validierungsfehler',
            detail: 'Bitte prüfen Sie die markierten Zeiten.',
            life: 5000,
        });
        return;
    }
    
    form.put(route('management.providers.update-working-hours', props.selectedProvider.uuid), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Arbeitszeiten wurden erfolgreich gespeichert.',
                life: 5000,
            });
        },
        onError: (errors) => {
            if (errors && typeof errors === 'object') {
                Object.keys(errors).forEach(field => {
                    const message = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
                    toast.add({
                        severity: 'error',
                        summary: 'Fehler',
                        detail: message,
                        life: 5000,
                    });
                });
            } else {
                toast.add({
                    severity: "error",
                    summary: "Fehler beim Speichern",
                    life: 5000,
                });
            }
        }
    });
};

</script>

<template>
    <Layout :flash="flash">
        <Head title="Arbeitszeiten Hub" />

        <div class="space-y-6">
            <ManagementHeader title="Mitarbeiter Management" module="providers" :activeIndex="1" />

            <!-- Top Selector Section -->
            <div class="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                        <i class="pi pi-users text-gray-400" />
                    </div>
                    <div>
                        <h3 class="font-bold text-gray-700">Mitarbeiter wählen</h3>
                        <p class="text-xs text-gray-400">Arbeitszeiten für Teammitglieder verwalten</p>
                    </div>
                </div>
                
                <div class="w-full md:w-80">
                    <Dropdown 
                        v-model="selectedProviderUuid" 
                        @change="handleProviderChange"
                        :options="providers" 
                        optionLabel="name" 
                        optionValue="uuid" 
                        placeholder="Mitarbeiter suchen..." 
                        filter 
                        class="w-full rounded-xl border-gray-200"
                    >
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ slotProps.option.name }}</span>
                            </div>
                        </template>
                    </Dropdown>
                </div>
            </div>

            <!-- Content Area -->
            <div v-if="selectedProvider" class="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div class="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <!-- Provider Info Bar -->
                    <div class="px-8 py-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                        <div class="flex items-center gap-4">
                            <div class="w-12 h-12 rounded-2xl bg-black flex items-center justify-center text-[#FFF053] font-bold text-xl">
                                {{ selectedProvider.name.charAt(0) }}
                            </div>
                            <div>
                                <h2 class="text-xl font-bold text-gray-800">{{ selectedProvider.name }}</h2>
                                <p class="text-sm text-gray-400">{{ selectedProvider.email || 'Keine E-Mail hinterlegt' }}</p>
                            </div>
                        </div>
                    </div>

                    <form :key="selectedProvider?.uuid" @submit.prevent="submit">
                        <Scheduler 
                            v-model="form.working_hours" 
                            :companyHours="selectedProvider.company?.working_hours"
                            title="Wöchentliche Arbeitszeiten" 
                            :show-customer-hours="true"
                            :errors="form.errors"
                            @clear-error="(key) => form.clearErrors(key)"
                        />
                        
                        <div class="px-8 py-6 bg-gray-50 border-t border-gray-100 flex justify-end">
                            <Button type="submit" 
                                    class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                                    :loading="form.processing" :disabled="form.processing">
                                <i class="pi pi-save" />
                                Arbeitszeiten speichern
                            </Button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="py-20 text-center bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
                <div class="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
                    <i class="pi pi-search text-2xl text-gray-300" />
                </div>
                <h3 class="text-lg font-bold text-gray-400">Kein Mitarbeiter ausgewählt</h3>
                <p class="text-gray-300">Bitte wählen Sie oben einen Mitarbeiter aus, um die Arbeitszeiten zu bearbeiten.</p>
            </div>
        </div>
    </Layout>
</template>
